import { useSearchParams } from 'react-router-dom';
import { useLoading } from 'contexts/loading';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { getSeizureReportCommunication, getUpdatesCommunication } from 'services/communication';
import {
  CommunicationUpdate,
  DateCommunication,
  fileResCommunication,
  headerTab,
  TAB
} from './type';
import dayjs from 'dayjs';
import { SIZE_PAGE_COMMUNICATION } from 'const/paging';
import { compact } from 'lodash';
import { renderTitleLead } from 'utils/renderTitleLead';
import { Lead } from 'types/user';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { deleteNoteByFile } from 'services/files';

export const useInjection = () => {
  const { loading } = useLoading();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setFlash } = useFlash();
  const [dataClientSelected, setDataClientSelected] = useState<fileResCommunication | undefined>(
    undefined
  );
  const [communicationUpdate, setCommunicationUpdate] = useState<CommunicationUpdate>({
    total: 0,
    updates: []
  });
  const [communicationSeizure, setCommunicationSeizure] = useState<CommunicationUpdate>({
    total: 0,
    updates: []
  });
  const [isDeleteNote, setIdDeleteNote] = useState<number | null>(null);
  const [leadDelete, setLeadDelete] = useState<Partial<Lead> | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearchText] = useDebounce(searchText, 800);

  const [dates, setDates] = useState<DateCommunication>({
    updates: null,
    seizureReports: null,
    outOfLeads: null
  });
  const currentTab = useMemo(() => {
    const params = searchParams.get('tab');
    return headerTab.find((item) => item.param === params)?.value || 0;
  }, [searchParams]);

  const fetchDataCommunicationUpDate = useCallback(async () => {
    const tab = searchParams.get('tab') || TAB.UPDATES;
    const currentDate = dates[tab as keyof typeof dates] as {
      startDate?: string | null;
      endDate?: string | null;
    } | null;

    const startDate =
      currentDate?.startDate && dayjs(currentDate.startDate).isValid()
        ? currentDate.startDate
        : undefined;
    const endDate =
      currentDate?.endDate && dayjs(currentDate.endDate).isValid()
        ? currentDate.endDate
        : undefined;

    if (tab === TAB.SEIZURE_REPORTS) {
      const data = await getSeizureReportCommunication({
        page: 1,
        size: SIZE_PAGE_COMMUNICATION,
        endDate,
        startDate,
        searchText: debouncedSearchText || undefined
      });
      setCommunicationSeizure(data);
      return;
    }
    if (tab === TAB.UPDATES) {
      const data = await getUpdatesCommunication({
        tab: tab,
        page: 1,
        size: SIZE_PAGE_COMMUNICATION,
        endDate,
        startDate,
        searchText: debouncedSearchText || undefined
      });
      setDataClientSelected(undefined);
      setCommunicationUpdate(data);
    }
  }, [searchParams, debouncedSearchText, dates]);

  useEffect(() => {
    fetchDataCommunicationUpDate();
  }, [dates, debouncedSearchText, searchParams]);

  const fetchDataCommunication = useCallback(async () => {
    const [dataUpdate, dataSeizure] = await Promise.all([
      getUpdatesCommunication({ page: 1, size: SIZE_PAGE_COMMUNICATION, tab: TAB.UPDATES }),
      getSeizureReportCommunication({ page: 1, size: SIZE_PAGE_COMMUNICATION }),
      getUpdatesCommunication({ page: 1, size: SIZE_PAGE_COMMUNICATION, tab: TAB.OUT_OF_LEADS })
    ]);
    setCommunicationUpdate(dataUpdate);
    setCommunicationSeizure(dataSeizure);
    if (dataUpdate?.updates?.length) {
      setDataClientSelected(dataUpdate?.updates[0]);
    }
  }, []);

  useEffect(() => {
    fetchDataCommunication();
  }, []);

  const handleChangeTab = useCallback(
    (newValue: number) => {
      const nameTab = headerTab.find((item) => item.value === newValue)?.param;
      setSearchParams({ tab: nameTab || TAB.UPDATES });
    },
    [setSearchParams]
  );

  const handleChangeDate = useCallback(
    ({ endDate, startDate }: { startDate?: string | null; endDate?: string | null }) => {
      const tab = searchParams.get('tab') || TAB.UPDATES;
      setDates((prevState) => ({
        ...prevState,
        [tab]: { startDate, endDate }
      }));
    },
    [searchParams]
  );

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  const handleCopyAllUpdates = useCallback(async (fileToCopy?: fileResCommunication) => {
    if (!fileToCopy) {
      alert('Cannot copy note (no file selected)');
      return;
    }

    const textCopyArray = compact(
      fileToCopy?.leads.map((itemLead) => {
        const titleLead = renderTitleLead(itemLead);
        const notes = compact(
          itemLead?.notes?.map(
            (itemNote) =>
              `${dayjs(itemNote.updatedAt).format('M/D/YYYY, h:mm A')} - ${itemNote.content}`
          )
        );
        if (!notes.length) return;
        return `${titleLead}\n${notes.join('\n')}`;
      })
    );

    if (!textCopyArray.length) {
      alert('Cannot copy note');
      return;
    }

    try {
      await navigator.clipboard.writeText(textCopyArray.join('\n\n'));
    } catch (error) {
      console.error('Failed to copy text to clipboard: ', error);
    }
  }, []);

  const handleDeleteAllNote = useCallback(
    async (indexRow: number) => {
      await deleteNoteByFile(indexRow);
      await fetchDataCommunicationUpDate();
      setFlash({
        message: 'Deleted successful',
        type: 'success'
      });
    },
    [dates, fetchDataCommunicationUpDate]
  );

  const { asyncCallback: asyncHandleDeleteAllNote } = useAsyncCallback(handleDeleteAllNote, []);

  const handleCopyAllSeizure = useCallback(async (fileToCopy?: fileResCommunication) => {
    let countIndex = 0;
    if (!fileToCopy) {
      alert('Cannot copy seizure report (no file selected)');
      return;
    }
    const textCopyArray = compact(
      fileToCopy.seizureReports?.map((item) => {
        const content = `
            ******** SEIZURE REPORT ********
            File Number: ${fileToCopy.fileNumber}
            Debtor Name: ${fileToCopy.firstName} ${fileToCopy.lastName}
            Seize Date: ${dayjs(item?.createdAt).format('YYYY/MM/DD')}
            Seized Address: ${item?.postalCode || ''} ${item?.addressLead?.address || item?.otherAddress}
            Customers POR: ${item?.customersLead?.address || item?.otherCustomerPOR}
            Removed to: ${item?.removedToLocation?.value || item?.removedToOther}
            KM: ${item?.kilometers}
            Keys: ${item?.hasKey ? 'Yes' : 'No'}
            Runs: ${item?.run ? 'Yes' : 'No'}
            Signed VSA: ${item.signedVsa ? 'Yes' : 'No'}
            Possible Redemption: ${item?.possibleRedemption ? 'Yes' : 'No'}
            Condition Report: ${item?.conditionReport}
            `;
        if (!content.length) return;
        countIndex++;
        return `${countIndex}. ${content}`;
      })
    );
    if (!textCopyArray.length) {
      alert('Cannot copy note');
      return;
    }
    try {
      await navigator.clipboard.writeText(textCopyArray.join('\n'));
    } catch (error) {
      console.error('Failed to copy text to clipboard: ', error);
    }
  }, []);

  const handleDeleteNote = ({ idNote, lead }: { idNote: number; lead?: Partial<Lead> }) => {
    setIdDeleteNote(idNote);
    setLeadDelete(lead);
  };

  return {
    tab: currentTab,
    searchParams,
    loading,
    handleChangeTab,
    handleChangeDate,
    dates,
    searchText,
    setSearchText,
    handleSearch,
    handleDeleteAllNote: asyncHandleDeleteAllNote,
    communicationSeizure,
    fetchDataCommunication,
    handleDeleteNote,
    leadDelete,
    setLeadDelete,
    isDeleteNote,
    setIdDeleteNote,
    handleCopyAllSeizure,
    onRefreshTabSeizureReport: async () => {
      const dataSeizure = await getSeizureReportCommunication({
        page: 1,
        size: SIZE_PAGE_COMMUNICATION,
        endDate: dates.seizureReports?.endDate,
        startDate: dates.seizureReports?.startDate
      });
      setCommunicationSeizure(dataSeizure);
    },
    updated: {
      dataClientSelected,
      setDataClientSelected,
      handleCopyAllUpdates,
      communicationUpdate
    }
  };
};
